import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useResizeObserver(delay = 100) {
  const observerRef = useRef<ResizeObserver>();
  const animationFrameRef = useRef<number>();
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState<DOMRect>();

  const update = (value: DOMRect) => {
    setIsResizing(false);
    setSize(value.toJSON());
  };

  const debouncedValue = useMemo(() => debounce(update, delay), [delay]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const containerRefCallback = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref) return;

      setSize({
        ...ref.getBoundingClientRect().toJSON(),
        width: ref.clientWidth,
        height: ref.clientHeight,
      });

      observerRef.current = new ResizeObserver((entries) => {
        animationFrameRef.current = requestAnimationFrame(() => {
          if (!isEmpty(entries)) {
            setIsResizing(true);

            const entry = entries[0];
            debouncedValue(entry.contentRect);
          }
        });
      });

      observerRef.current.observe(ref);
    },
    [debouncedValue],
  );

  return {
    containerRef: containerRefCallback,
    size: size,
    isResizing: isResizing,
  };
}
